// MATERIAL UI
import { Button, Grid, Typography } from "@mui/material";

// AMPLITUTE
import { trackEvent } from "src/Amplitude";

// REACT
import React from "react";
import { useLocation } from "react-router-dom";

// STYLES
import { SelectCurrencyStyles } from "../common/styles/SelectCurrency.styles";

// TYPES
import { DropdownButtonProps } from "../common/types/dropdownTypes";

// COMPONENTS
import ModalTransition from "../Modal/ModalTransition";
import DropdownBody from "./DropdownBody";
import DropdownList from "./DropdownList";
import { VerificationBanner } from "src/components/core/VerificationBanner";
import MaintananceTooltip from "src/components/maintenance/MaintenanceToolTip";

// HOOKS
import usePermission from "src/utils/hooks/usePermission";
import { useMaintenanceState } from "src/store/features/maintenance.slice";
import {
  CloseButton,
  Header,
  HeaderAndSearch,
  Wrapper,
} from "src/components/common/crypto-selection-modalV2/CryptoSelectionModalV2";
import CloseIcon from "@mui/icons-material/Close";

const DropdownButton = (props: DropdownButtonProps): JSX.Element => {
  const classes = SelectCurrencyStyles();
  const config = props.config;
  const button = props.config.button;
  const location = useLocation();

  // GLOBAL CONTEXT
  const { depositcontroller } = useMaintenanceState();
  const [open, setOpen] = React.useState<boolean>(false);

  React.useEffect(() => {
    if (location.pathname === "/wallets/deposit") {
      handleModalToggle("Deposit");
    }
  }, [location]);

  const handleModalToggle = (page: string): void => {
    trackEvent("Visit_Page", { page: page });
    setOpen((prev: boolean): boolean => !prev);
  };

  const handleClose = (reason: any) => {
    if (reason === "clickaway") {
      return;
    }
    setOpen(false);
  };

  const { userPermission } = usePermission();
  const isGuest = userPermission === "guest";

  const title = "Unlock the Full Potential of Our Platform, Become a Verified User";
  const description =
    "As a verified user on our platform, you're given exclusive access to our seamless Fiat to Crypto exchange feature. This service enables you to directly deposit Fiat currency using the provided banking details, which will be conveniently displayed in your wallet balance. With these credits, buying and selling crypto becomes a streamlined, user-friendly process.";

  return (
    <>
      <MaintananceTooltip
        message="Deposits are undergoing maintenance. If you need assistance, please contact support"
        disableHover={depositcontroller}>
        <Button
          disableRipple
          onClick={() => {
            trackEvent("Select_Wallets_Page_Deposit");
            handleModalToggle("Deposit");
          }}
          fullWidth={button.fullWidth ? button.fullWidth : false}
          variant="outlined"
          className={button.className ? button.className : classes.buttonContainer}
          size="large"
          endIcon={button.endIcon}
          startIcon={button.startIcon}
          disabled={button.disabled || !depositcontroller}>
          <Typography className={classes.buttonText}>{button.title}</Typography>
        </Button>
      </MaintananceTooltip>
      {isGuest && (
        <VerificationBanner
          open={open}
          onClose={() => setOpen(false)}
          title={title}
          message={description}
        />
      )}
      {!isGuest && (
        <Wrapper
          open={open}
          TransitionComponent={ModalTransition}
          onClose={handleClose}
          aria-describedby="alert-dialog-slide-description"
          PaperProps={{ className: classes.paperContainer }}
          sx={{ "& .MuiDialog-paper": { height: "fit-content" } }}>
          <HeaderAndSearch>
            {/* header */}
            <Header>
              {config.subtitle}
              {/* close button */}
              <CloseButton onClick={handleClose}>
                <CloseIcon fontSize="inherit" />
              </CloseButton>
            </Header>
          </HeaderAndSearch>

          {/* SEARCH BAR AND CONTENT */}
          <Grid item xs={12} container justifyContent="center">
            {config.searchable ? (
              <DropdownBody data={config.data} config={config} />
            ) : (
              <DropdownList data={config.data} onClick={config.onClick} />
            )}
          </Grid>
        </Wrapper>
      )}
    </>
  );
};

export default DropdownButton;
